import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';
import type { AgenticTestResultDto, WorkflowTestRunStatus } from './useAgenticRunDetail';
import type { VideoTimeline } from '@chatsdet/types';
import type { WrongOrgInfo } from '../../ui/components/OrgSwitchPrompt';
import type { TestRunRerunAvailability } from './rerunAvailability';

// Test run with results
export type FailedAttemptSummary = {
  errorMessage: string | null;
  failedNodeName: string | null;
  failureScreenshotS3Key: string | null;
  failureContext: unknown;
};

type TestRunDetailDto = {
  id: string;
  executionId: string | null;
  workflowId: string;
  status: WorkflowTestRunStatus;
  snapshotVersion: number;
  workflowRunId: string | null;
  triggeredByUserId: string | null;
  triggeredReason: string | null;
  linkedIssueId: string | null;
  errorMessage: string | null;
  skipReason: string | null;
  metadataJson: {
    isFlaky?: boolean;
    hadRetries?: boolean;
    failedAttempt?: FailedAttemptSummary;
  } | null;
  scenarioId: string | null;
  scenarioName: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  rerunAvailability: TestRunRerunAvailability;
  results: AgenticTestResultDto[];
};

// Workflow run step
export type WorkflowRunStepDto = {
  id: string;
  runId: string;
  nodeId: string;
  status: 'pending' | 'running' | 'waiting' | 'success' | 'failed' | 'skipped';
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  screenshotS3Key: string | null;
  metadataJson: Record<string, unknown> | null;
  createdAt: string;
};

export type NoVideoReason =
  | 'no_browser_interaction'
  | 'api_only_workflow'
  | 'run_failed_before_recording'
  | 'session_end_failed'
  | 'recording_failed';

// Workflow run with steps
type WorkflowRunDto = {
  id: string;
  workflowId: string;
  orgId: string;
  status: 'queued' | 'running' | 'success' | 'failed' | 'canceled';
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  metadataJson: Record<string, unknown> | null;
  createdAt: string;
  steps: WorkflowRunStepDto[];
  videoStorageKey: string | null;
  videoTimelineJson: VideoTimeline | null;
  noVideoReason: NoVideoReason | null;
  traceStorageKey: string | null;
};

// Workflow node (from snapshot)
export type WorkflowNodeDto = {
  id: string;
  type?: string;
  position?: { x: number; y: number };
  data?: Record<string, unknown>;
};

// Workflow metadata
type WorkflowSummaryDto = {
  id: string;
  name: string;
  nodes?: WorkflowNodeDto[];
};

// Attempt summary
type AttemptSummaryDto = {
  id: string;
  status: WorkflowTestRunStatus;
  startedAt: string | null;
  finishedAt: string | null;
  snapshotVersion: number;
  isCurrent: boolean;
};

// API response type
type TestRunDetailResponse = {
  ok: boolean;
  testRun: TestRunDetailDto;
  workflow: WorkflowSummaryDto | null;
  workflowRun: WorkflowRunDto | null;
  attempts: AttemptSummaryDto[];
};

/**
 * Fetch test run detail with workflow run data and attempts
 */
export function useTestRunDetail(testRunId: string | undefined) {
  const [wrongOrgInfo, setWrongOrgInfo] = React.useState<WrongOrgInfo | null>(null);

  const query = useQuery<TestRunDetailResponse>({
    queryKey: ['test-run-detail', testRunId],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/workflows/test-runs/${testRunId}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'TEST_RUN_WRONG_ORG' && errorData.targetOrgId && errorData.targetOrgName) {
          setWrongOrgInfo({
            orgId: errorData.targetOrgId,
            orgName: errorData.targetOrgName,
          });
        }
        throw new Error(errorData.error || 'Failed to load test run details');
      }

      setWrongOrgInfo(null);
      return response.json();
    },
    enabled: !!testRunId,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.testRun?.status;
      // Auto-refresh while running or queued
      return status === 'running' || status === 'queued' ? 3000 : false;
    },
  });

  const clearWrongOrgInfo = React.useCallback(() => setWrongOrgInfo(null), []);

  return { ...query, wrongOrgInfo, clearWrongOrgInfo };
}

/**
 * Format duration between two timestamps
 */
export function formatDuration(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
}

/**
 * Format relative time ago
 */
export function formatRelativeTimeAgo(value?: string | null): string {
  if (!value) return 'Unknown';
  const date = new Date(value);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) return 'Unknown';

  const deltaMs = Date.now() - timestamp;
  const deltaSeconds = Math.floor(Math.abs(deltaMs) / 1000);
  const future = deltaMs < 0;

  if (deltaSeconds < 10) return future ? 'in a few seconds' : 'just now';
  if (deltaSeconds < 60) return future ? `in ${deltaSeconds}s` : `${deltaSeconds}s ago`;

  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) return future ? `in ${deltaMinutes}m` : `${deltaMinutes}m ago`;

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) return future ? `in ${deltaHours}h` : `${deltaHours}h ago`;

  const deltaDays = Math.floor(deltaHours / 24);
  return future ? `in ${deltaDays}d` : `${deltaDays}d ago`;
}
